import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const ADMIN_STEPS = [
  { anchor: null, title: 'Welcome to ONRID 👋', body: 'Your school runs on modules - here is a 60-second tour of the essentials: licensing, fees and school-wide announcements.' },
  { anchor: '[data-testid="nav-admin-center"]', title: 'Admin Center - Modules', body: 'The Modules tab shows what your school has purchased. Advanced modules (Exams, Library, LMS, University) can run on 30-day trials before purchase.' },
  { anchor: '[data-testid="nav-fees-finance"]', title: 'Fees & Finance', body: 'Build fee plans, generate invoices, collect payments (Stripe), track dues aging and email guardian statements - all from one place.' },
  { anchor: '[data-testid="nav-communication"]', title: 'Communication', body: 'Versioned templates and announcements that reach guardians by in-app notification and email (once your Brevo key is set in Settings).' },
  { anchor: '[data-testid="nav-announcements"]', title: 'Broadcasts', body: 'Urgent school-wide banners with grade targeting, scheduling and per-recipient delivery reports. Compose them in Admin Center - Broadcast.' },
];

const TOURS = {
  school_admin: ADMIN_STEPS,
  global_admin: ADMIN_STEPS,
  teacher: [
    { anchor: null, title: 'Welcome, teacher 👋', body: 'Here is a quick tour of your daily tools - attendance, marks, lessons and exams.' },
    { anchor: '[data-testid="nav-attendance"]', title: 'Attendance', body: 'Mark today\'s class attendance in seconds. Offline-sync conflicts are flagged for review instead of silently overwriting.' },
    { anchor: '[data-testid="nav-academics"]', title: 'Academics & marks', body: 'Enter assessment marks, manage the timetable and generate report cards for your classes.' },
    { anchor: '[data-testid="nav-lms"]', title: 'LMS lessons', body: 'Post lessons with PDFs, Word docs, videos and YouTube/Vimeo embeds. Create assignments and grade submissions.' },
    { anchor: '[data-testid="nav-exams"]', title: 'Exams', body: 'Enter exam marks and teacher remarks. Once the principal publishes results, branded report card PDFs go out to guardians.' },
  ],
  guardian: [
    { anchor: null, title: 'Welcome 👋', body: 'Everything about your child in one place - fees, library books, messages and school announcements.' },
    { anchor: '[data-testid="nav-fees-finance"]', title: 'Fees', body: 'See invoices and receipts, pay online, download fee statements - and request an installment split on a large invoice.' },
    { anchor: '[data-testid="nav-chat"]', title: 'Chat', body: 'Message your child\'s teachers directly, with read receipts. Teachers can share consent-scoped fee statements here too.' },
    { anchor: '[data-testid="nav-library"]', title: 'Library', body: 'Track the books your child has borrowed and due dates. You\'ll be notified the day a book turns overdue.' },
    { anchor: '[data-testid="nav-announcements"]', title: 'Announcements', body: 'School broadcasts and notices appear here and as banners - you\'ll never miss an urgent closure notice.' },
  ],
};

export const OnboardingTour = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const key = user?.id ? `or_tour_done_${user.id}` : null;
  const STEPS = TOURS[user?.role] || null;

  useEffect(() => {
    if (user && STEPS && key && !localStorage.getItem(key)) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [user, key, STEPS]);

  useEffect(() => {
    if (!open || !STEPS) return;
    const s = STEPS[step];
    const el = s.anchor ? document.querySelector(s.anchor) : null;
    setRect(el ? el.getBoundingClientRect() : null);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [open, step, STEPS]);

  if (!open || !STEPS) return null;
  const s = STEPS[step];
  const done = () => { if (key) localStorage.setItem(key, '1'); setOpen(false); };
  const cardStyle = rect
    ? { position: 'fixed', top: Math.min(rect.top, window.innerHeight - 240), left: Math.min(rect.right + 16, window.innerWidth - 360), width: 340 }
    : { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 380 };

  return (
    <div className="fixed inset-0 z-[80]" data-testid="onboarding-tour">
      <div className="absolute inset-0 bg-slate-900/50" onClick={done} data-testid="tour-backdrop" />
      {rect && (
        <div className="absolute rounded-lg ring-4 ring-orange-500 ring-offset-2 pointer-events-none transition-all duration-300"
          style={{ top: rect.top - 4, left: rect.left - 4, width: rect.width + 8, height: rect.height + 8 }} />
      )}
      <div className="bg-white rounded-xl shadow-2xl p-5 z-[81]" style={cardStyle} data-testid="tour-card">
        <p className="font-mono-label text-[10px] uppercase text-orange-600 mb-1">Step {step + 1} of {STEPS.length}</p>
        <h3 className="font-display font-bold text-lg mb-1" data-testid="tour-title">{s.title}</h3>
        <p className="text-sm text-slate-600 mb-4">{s.body}</p>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 me-auto">
            {STEPS.map((st, i) => <span key={st.title} className={`w-1.5 h-1.5 rounded-full ${i === step ? 'bg-orange-500' : 'bg-slate-200'}`} />)}
          </div>
          <button data-testid="tour-skip" onClick={done} className="text-xs text-slate-400 hover:text-slate-600">Skip tour</button>
          {step > 0 && <Button data-testid="tour-back" size="sm" variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          {step < STEPS.length - 1
            ? <Button data-testid="tour-next" size="sm" className="bg-orange-600 hover:bg-orange-500" onClick={() => setStep(step + 1)}>Next</Button>
            : <Button data-testid="tour-finish" size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={done}>Finish</Button>}
        </div>
      </div>
    </div>
  );
};
